import { useEffect, useState } from 'react';
import {
  IonPage, IonHeader, IonToolbar, IonTitle, IonContent, IonButtons, IonMenuButton,
  IonList, IonItem, IonLabel, IonButton, IonChip, IonSpinner, IonSelect, IonSelectOption,
  IonModal, IonInput, IonIcon,
} from '@ionic/react';
import { refreshOutline, addOutline, closeOutline } from 'ionicons/icons';
import { PAYMENT_STATUSES } from '@ai-media/shared';
import { useTranslation } from 'react-i18next';
import { api, apiErrorMessage } from '@/lib/api';
import { toast } from '@/store/ui.store';

interface AdminPayment {
  id: string;
  userId: string;
  ownerEmail: string;
  plan: string;
  method: string;
  status: string;
  amountPaise: number;
  creditsGranted: number;
  provider: string;
  createdAt: string;
  paidAt: string | null;
}

interface PaymentsResponse {
  data: AdminPayment[];
  revenuePaise: number;
  page: number;
  limit: number;
  total: number;
  hasMore: boolean;
}

/** Paise → rupees for display only; the API contract stays in paise. */
const inr = (paise: number) =>
  (paise / 100).toLocaleString('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 2,
  });

const statusLabel = (value: string) =>
  value === 'past_due' ? 'Past due' : value.charAt(0).toUpperCase() + value.slice(1);

const paymentColor = (value: string): 'success' | 'danger' | 'warning' | 'medium' => {
  if (value === 'captured') return 'success';
  if (value === 'failed') return 'danger';
  if (value === 'authorized' || value === 'refunded') return 'warning';
  return 'medium';
};

export default function BillingAdminPage() {
  const { t } = useTranslation();
  const [payments, setPayments] = useState<AdminPayment[]>([]);
  const [revenuePaise, setRevenuePaise] = useState(0);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjustUserId, setAdjustUserId] = useState('');
  const [adjustAmount, setAdjustAmount] = useState('');
  const [adjustNote, setAdjustNote] = useState('');
  const [saving, setSaving] = useState(false);
  const limit = 20;

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get<PaymentsResponse>('/admin/payments', {
        params: { status: status || undefined, page, limit },
      });
      setPayments(data.data);
      setRevenuePaise(data.revenuePaise);
      setTotal(data.total);
    } catch (error) {
      toast(apiErrorMessage(error), 'danger');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, [status, page]);

  const closeAdjust = () => {
    setAdjustOpen(false);
    setAdjustUserId('');
    setAdjustAmount('');
    setAdjustNote('');
  };

  const submitAdjust = async () => {
    const amount = Number.parseInt(adjustAmount, 10);
    if (!adjustUserId.trim() || !Number.isFinite(amount) || amount === 0) {
      toast(t('admin.adjustInvalid'), 'warning');
      return;
    }
    setSaving(true);
    try {
      await api.post(`/admin/users/${adjustUserId.trim()}/credits`, {
        amount,
        note: adjustNote.trim() || undefined,
      });
      toast(t('admin.creditsAdjusted'), 'success');
      closeAdjust();
    } catch (error) {
      toast(apiErrorMessage(error), 'danger');
    } finally {
      setSaving(false);
    }
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start"><IonMenuButton /></IonButtons>
          <IonTitle>{t('admin.billingAdmin')}</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={() => void load()}><IonIcon slot="icon-only" icon={refreshOutline} /></IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        {loading && payments.length === 0 ? (
          <div className="empty-state"><IonSpinner /></div>
        ) : (
          <div className="page-shell page-shell--flush">
            <h6 className="section-title">{t('admin.billingAdminSubtitle')}</h6>

            <div className="kpi-grid">
              <div className="kpi-tile">
                <div className="kpi-label">{t('admin.revenue')}</div>
                <div className="kpi-value" style={{ fontSize: '1.1rem' }}>{inr(revenuePaise)}</div>
              </div>
              <div className="kpi-tile">
                <div className="kpi-label">{t('admin.payments')}</div>
                <div className="kpi-value" style={{ fontSize: '1.1rem' }}>{total}</div>
              </div>
              <div className="kpi-tile">
                <div className="kpi-label">{t('admin.adjustCredits')}</div>
                <IonButton size="small" style={{ marginTop: 8 }} onClick={() => setAdjustOpen(true)}>
                  <IonIcon slot="start" icon={addOutline} />
                  {t('admin.adjustCredits')}
                </IonButton>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
              <IonSelect
                placeholder={t('admin.allStatuses')}
                value={status}
                onIonChange={(e) => {
                  setStatus(e.detail.value as string);
                  setPage(1);
                }}
                interface="popover"
              >
                <IonSelectOption value="">{t('admin.allStatuses')}</IonSelectOption>
                {PAYMENT_STATUSES.map((s) => (
                  <IonSelectOption key={s} value={s}>{statusLabel(s)}</IonSelectOption>
                ))}
              </IonSelect>
            </div>

            <IonList inset>
              {payments.length === 0 ? (
                <IonItem lines="none" detail={false} button={false}>
                  <IonLabel>{t('admin.noResults')}</IonLabel>
                </IonItem>
              ) : (
                payments.map((p) => (
                  <IonItem key={p.id} lines="full">
                    <IonLabel>
                      <h3>{p.ownerEmail}</h3>
                      <p style={{ color: 'var(--ion-color-medium)', fontSize: '0.85rem' }}>
                        {p.plan} · {p.method} · {p.provider} · {new Date(p.createdAt).toLocaleDateString()}
                      </p>
                    </IonLabel>
                    <IonChip color={paymentColor(p.status)}>{statusLabel(p.status)}</IonChip>
                    <IonLabel style={{ textAlign: 'right', minWidth: 90 }}>
                      <h3>{inr(p.amountPaise)}</h3>
                      <p style={{ color: 'var(--ion-color-medium)', fontSize: '0.85rem' }}>
                        +{p.creditsGranted} cr
                      </p>
                    </IonLabel>
                  </IonItem>
                ))
              )}
            </IonList>

            {total > limit && (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0 16px', marginTop: 16 }}>
                <span style={{ fontSize: '0.9rem', color: 'var(--ion-color-medium)' }}>
                  Page {page} of {Math.ceil(total / limit)}
                </span>
                <div style={{ display: 'flex', gap: 8 }}>
                  <IonButton size="small" disabled={page === 1} onClick={() => setPage(page - 1)}>
                    {t('common.previous')}
                  </IonButton>
                  <IonButton size="small" disabled={page * limit >= total} onClick={() => setPage(page + 1)}>
                    {t('common.next')}
                  </IonButton>
                </div>
              </div>
            )}
          </div>
        )}
        <IonModal isOpen={adjustOpen} onDidDismiss={closeAdjust}>
          <IonHeader>
            <IonToolbar>
              <IonButtons slot="start">
                <IonButton onClick={closeAdjust}>
                  <IonIcon slot="icon-only" icon={closeOutline} />
                </IonButton>
              </IonButtons>
              <IonTitle>{t('admin.adjustCredits')}</IonTitle>
            </IonToolbar>
          </IonHeader>
          <IonContent>
            <div style={{ padding: '12px 16px 32px', display: 'flex', flexDirection: 'column', gap: 12 }}>
              <p style={{ fontSize: '0.9rem', color: 'var(--ion-color-medium)' }}>
                {t('admin.adjustCreditsHint')}
              </p>
              <IonItem>
                <IonLabel position="stacked">{t('admin.userId')}</IonLabel>
                <IonInput
                  value={adjustUserId}
                  placeholder="00000000-0000-0000-0000-000000000000"
                  onIonInput={(e) => setAdjustUserId(String((e.target as HTMLIonInputElement).value ?? ''))}
                />
              </IonItem>
              <IonItem>
                <IonLabel position="stacked">{t('admin.amount')}</IonLabel>
                <IonInput
                  type="number"
                  value={adjustAmount}
                  placeholder="+50 / -50"
                  onIonInput={(e) => setAdjustAmount(String((e.target as HTMLIonInputElement).value ?? ''))}
                />
              </IonItem>
              <IonItem>
                <IonLabel position="stacked">{t('admin.note')}</IonLabel>
                <IonInput
                  value={adjustNote}
                  onIonInput={(e) => setAdjustNote(String((e.target as HTMLIonInputElement).value ?? ''))}
                />
              </IonItem>
              <IonButton expand="block" disabled={saving} onClick={() => void submitAdjust()}>
                {saving ? <IonSpinner name="dots" /> : t('admin.apply')}
              </IonButton>
            </div>
          </IonContent>
        </IonModal>
      </IonContent>
    </IonPage>
  );
}